import { reportException } from 'src/utils/errorReporting';

export type SettledQuery<T> =
  | { ok: true; result: T }
  | { ok: false; error: unknown };

const QUERY_LIFECYCLE_ERROR_PREFIX = 'RTK Query';
const SERVER_ERROR_STATUS_CUTOFF = 500;

const getNumericStatus = (value: unknown): number | undefined => {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  if (typeof record.status === 'number') {
    return record.status;
  }

  if (
    record.response &&
    typeof record.response === 'object' &&
    typeof (record.response as { status?: unknown }).status === 'number'
  ) {
    return (record.response as { status: number }).status;
  }

  return undefined;
};

export const getQueryLifecycleHttpStatus = (
  err: unknown,
): number | undefined => {
  const fromRoot = getNumericStatus(err);
  if (fromRoot != null) {
    return fromRoot;
  }

  if (typeof err === 'object' && err !== null && 'error' in err) {
    return getNumericStatus((err as { error: unknown }).error);
  }

  return undefined;
};

// Lets an engineer jump from the report to the backend logs and trace for the
// failed request; absent when the request never reached the backend.
const getRequestId = (err: unknown): string | undefined => {
  if (typeof err !== 'object' || err === null || !('error' in err)) {
    return undefined;
  }
  const { requestId } = (err as { error: { requestId?: unknown } }).error ?? {};
  return typeof requestId === 'string' && requestId ? requestId : undefined;
};

export const queryLifecycleErrorMessage = (endpoint: string): string =>
  `${QUERY_LIFECYCLE_ERROR_PREFIX} ${endpoint} failed`;

// Only 5xx is a backend failure worth paging. Aborts, offline, CORS, and
// 4xx (expired session, not a member) serialize as `{ error: {} }` or a
// client status and used to mint one Linear ticket per boot query.
export const shouldReportQueryLifecycleFailure = (error: unknown): boolean => {
  const status = getQueryLifecycleHttpStatus(error);
  return status != null && status >= SERVER_ERROR_STATUS_CUTOFF;
};

const reportQueryLifecycleFailure = (
  error: unknown,
  endpoint: string,
): void => {
  const status = getQueryLifecycleHttpStatus(error);
  if (status == null || status < SERVER_ERROR_STATUS_CUTOFF) {
    return;
  }

  const tags: Record<string, string> = {
    rtk_query_endpoint: endpoint,
    http_status: String(status),
  };
  const requestId = getRequestId(error);
  if (requestId) {
    tags.request_id = requestId;
  }

  reportException(new Error(queryLifecycleErrorMessage(endpoint)), {
    tags,
    extra: { originalError: error },
    fingerprint: ['rtk-query-lifecycle', endpoint],
  });
};

/**
 * Await an RTK Query lifecycle promise without ever rejecting. Server 5xx
 * failures are reported as a real Error tagged with the endpoint so they
 * group per endpoint instead of collapsing into one stackless rejection.
 */
export const settleQueryFulfilled = async <T>(
  queryFulfilled: Promise<T>,
  endpoint: string,
): Promise<SettledQuery<T>> => {
  try {
    return { ok: true, result: await queryFulfilled };
  } catch (error) {
    try {
      reportQueryLifecycleFailure(error, endpoint);
    } catch {
      // Reporting must not turn a settled failure into a new rejection.
    }
    return { ok: false, error };
  }
};
